import { createRoute } from '@tanstack/react-router';
import { Authenticate, layoutRoute, LoaderCtx, loaderFn } from '@/router/root-routes.tsx';
import { getProjectQuery } from '@/app/account/api/queries.ts';
import { TProject } from '@/types/models.ts';
import { Button } from '@/components/ui/button.tsx';
import { AppAssistanceButton } from '@/components/app/app-assistance-button.tsx';
import { useAuth } from '@/stores/auth.store.ts';
import { useMemo, useState } from 'react';
import { ProjectEdit } from '@/app/account/projects/project-edit.tsx';
import { ProjectItem } from '@/app/account/projects/project-item.tsx';
import { TLabelValue } from '@/types/misc.ts';
import { useUiStore } from '@/stores/ui.store.ts';
import { usePageProps } from '@/hooks/use-page-props.ts';

export const projectViewRoute = createRoute({
  getParentRoute: () => layoutRoute,
  path: 'account/projects/$id',
  component: ProjectViewPage,
  beforeLoad: Authenticate(),
  loader: loaderFn(async (ctx: LoaderCtx) => {
    return await ctx.context.queryClient.ensureQueryData(getProjectQuery(ctx.params.id)).catch((err: any) => {
      console.error('Error loading project data:', err);
      throw err;
    });
  }),
});

function ProjectViewPage() {
  const project = projectViewRoute.useLoaderData() as TProject;
  usePageProps({
    title: project.name
  })
  const uiUpdate = useUiStore(state => state.uiUpdate);
  const [showEdit, setShowEdit] = useState(false);
  const { user } = useAuth();
  // noinspection com.intellij.reactbuddy.ExhaustiveDepsInspection
  const areas = useMemo(() => {
    const areaSet = new Set<string>();
    project.items.forEach((item) => {
      if (item.area) {
        areaSet.add(item.area);
      }
    });
    const areaOpts: TLabelValue[] = [];
    areaSet.forEach((area) => {
      areaOpts.push({ label: area, value: area });
    });
    // areaOpts.push({ label: 'New Area', value: 'new_area' });
    return areaOpts;
  }, [project]);

  return (
    <div key={`project-view-${uiUpdate}`} className="max-w-8xl mx-auto">
      <div className="flex justify-between items-start py-3">
        <div>
          <h1 className="text-3xl">Project Details</h1>
          <h2 className="text-2xl">{project.name}</h2>
          {project.client && <p className="text-gray-600">Client: {project.client}</p>}
          {project.shipping_address?.one_line && (
            <p className="text-gray-600">Address: {project.shipping_address.one_line}</p>
          )}
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => setShowEdit(true)}>Update</Button>
          <AppAssistanceButton
            title="Project"
            message={`I am looking for help with project ${project.name}.`}
            subject={`New sample assistance request from ${user.full_name}`}
          />
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-5 gap-8 products-grid mb-8">
        {project.items.map((item) => (
          <ProjectItem key={item.id} project={project} item={item} areas={areas} />
        ))}
      </div>
      {showEdit && <ProjectEdit onClose={() => setShowEdit(false)} project={project} />}
    </div>
  );
}
